<script setup lang="ts">
import type { AcContactData } from "@/types/crm"
import AcMediumModal from '@/components/reusable/teleports/acMediumModal.vue'
import ContactModal from '@/components/reusable/contactModal.vue'
import { ref, computed } from 'vue'
import { modalClosed } from '@/mixins/visualFormatters'

const props = defineProps<{
    contactData?: AcContactData | AcContactData[]
    isMultiple?: boolean
    isEditable?: boolean
    isDeletable?: boolean
    showAddButton?: boolean
    addButtonText?: string
    emptyStateText?: string
    showIcons?: boolean
    showQrCode?: boolean
    contactQrTitle?: string
    cols?: number
}>()

const emits = defineEmits<{
    contactUpdated: [updatedContact: AcContactData, originalContact: AcContactData]
    contactDeleted: [contact: AcContactData]
    contactAdded: [contact: AcContactData]
}>()

// Normalize contact data to always be an array
const contacts = computed(() => {
    if (!props.contactData) return []
    return Array.isArray(props.contactData) ? props.contactData : [props.contactData]
})

const isAddingContact = ref(false)
const editingContact = ref<number | null>(null)
const showingQrCode = ref<number | null>(null)

// Contact field icons mapping
/* const fieldIcons = {
    title: '👤',
    emailAddress: '../../assets/icons/mail.png',
    phoneNumber: '../../assets/icons/phone.png',
    address: '../../assets/icons/pin.png',
    website: '../../assets/icons/internet.png'
}
 */
function startEdit(index: number) {
    if (props.isEditable) {
        editingContact.value = index
    }
}

/* function cancelEdit() {
    editingContact.value = null
} */

function deleteContact(contact: AcContactData) {
    emits('contactDeleted', contact)
}

function startAddContact() {
    isAddingContact.value = true
}

function handleContactUpdated(contact: AcContactData, index: number) {
    const originalContact = contacts.value[index]
    editingContact.value = null
    isAddingContact.value = false
    modalClosed()
    emits('contactUpdated', contact, originalContact)
}

function handleNewContactSaved(contact: AcContactData) {
    isAddingContact.value = false
    editingContact.value = null
    emits('contactAdded', contact)
    modalClosed()
}

function displayQrCode(index: number) {
    showingQrCode.value = index
}

function closeQrCode() {
    showingQrCode.value = null
}

// Generate vCard format for QR code
function generateVCard(contact: AcContactData): string {
    const vcard = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${contact.title}`,
        `N:${contact.title};;;;`,
    ]

    if (props.contactQrTitle) {
        vcard.push(`ORG:${props.contactQrTitle}`)
    }

    if (contact.emailAddress) {
        vcard.push(`EMAIL:${contact.emailAddress}`)
    }

    if (contact.phoneNumber) {
        vcard.push(`TEL:${contact.phoneNumber}`)
    }

    if (contact.address) {
        vcard.push(`ADR:;;${contact.address};;;;`)
    }

    if (contact.website) {
        vcard.push(`URL:${contact.website}`)
    }

    vcard.push('END:VCARD')
    return vcard.join('\n')
}

// Generate QR code URL using a QR service
function generateQrCodeUrl(contact: AcContactData): string {
    const vcard = generateVCard(contact)
    const encodedVCard = encodeURIComponent(vcard)
    // Using QR Server API for generating QR codes
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedVCard}`
}

// Helper to check if contact has any data
function hasContactInfo(contact: AcContactData): boolean {
    return !!(contact.emailAddress || contact.phoneNumber || contact.address || contact.website)
}

/* // Helper to get display value for field
function getFieldValue(contact: AcContactData, field: keyof AcContactData): string {
    return contact[field] || ''
}

// Helper to get field label
function getFieldLabel(field: keyof AcContactData): string {
    const labels = {
        title: 'İsim/Unvan',
        emailAddress: 'E-posta',
        phoneNumber: 'Telefon',
        address: 'Adres',
        website: 'Website'
    }
    return labels[field] || field
} */
</script>

<template>
    <div class="contact-displayer-wrapper">
        <!-- Contact Grid -->
        <div class="contact-grid" :style="{ gridTemplateColumns: `repeat(${cols || 1}, 1fr)` }">
            <!-- Empty State -->
            <div v-if="contacts.length === 0" class="empty-state">
                <div class="empty-icon">📞</div>
                <p class="empty-text">
                    {{ emptyStateText || "Henüz iletişim bilgisi eklenmemiş." }}
                </p>
            </div>

            <!-- Contact List -->
            <div v-for="(contact, index) in contacts" :key="index" class="contact-item">
                <div class="contact-card">
                    <!-- Contact Header -->
                    <div class="contact-header">
                        <div class="contact-title">
                            <h4>{{ contact.title }}</h4>
                        </div>

                        <!-- Action Buttons -->
                        <div class="contact-actions" v-if="isEditable || isDeletable">
                            <button v-if="isEditable" @click="startEdit(index)" class="action-btn edit-btn"
                                :title="'Düzenle'">
                                <img src="../../assets/icons/edit.png" alt="Edit" class="w-4 h-4 filter-main" />
                            </button>
                            <button v-if="isDeletable" @click="deleteContact(contact)" class="action-btn delete-btn"
                                :title="'Sil'">
                                <img src="../../assets/icons/delete.png" alt="Delete" class="w-4 h-4 filter-main">
                            </button>
                        </div>
                    </div>

                    <!-- Contact Details -->
                    <div class="contact-details" v-if="hasContactInfo(contact)">
                        <!-- Email -->
                        <div v-if="contact.emailAddress" class="contact-field">
                            <img src="../../assets/icons/mail.png" alt="Email" class="field-icon-img filter-main">
                            <a :href="`mailto:${contact.emailAddress}`" class="field-value email-link">
                                {{ contact.emailAddress }}
                            </a>
                        </div>

                        <!-- Phone -->
                        <div v-if="contact.phoneNumber" class="contact-field">
                            <img src="../../assets/icons/phone.png" alt="Phone" class="field-icon-img filter-main">
                            <a :href="`tel:${contact.phoneNumber}`" class="field-value phone-link">
                                {{ contact.phoneNumber }}
                            </a>
                        </div>

                        <!-- Website -->
                        <div v-if="contact.website" class="contact-field">
                            <img src="../../assets/icons/internet.png" alt="Website" class="field-icon-img filter-main">
                            <a :href="contact.website" target="_blank" class="field-value website-link">
                                {{ contact.website }}
                            </a>
                        </div>

                        <!-- Address -->
                        <div v-if="contact.address" class="contact-field">
                            <img src="../../assets/icons/pin.png" alt="Address" class="field-icon-img filter-main">
                            <span class="field-value">{{ contact.address }}</span>
                        </div>
                    </div>

                    <!-- No Contact Info State -->
                    <div v-else class="no-contact-info">
                        <p>Bu kişi için detaylı iletişim bilgisi bulunmuyor.</p>
                    </div>

                    <!-- QR Code Button -->
                    <div v-if="showQrCode && hasContactInfo(contact)" class="qr-button-container">
                        <button @click="displayQrCode(index)" class="ac-minimal-button qr-save-btn">
                            Telefona Kaydet
                        </button>
                    </div>
                </div>

                <!-- QR Code Modal -->
                <div v-if="showingQrCode === index" class="qr-overlay" @click="closeQrCode">
                    <div class="qr-modal" @click.stop>
                        <div class="qr-header">
                            <h3>{{ contact.title }} - İletişim QR Kodu</h3>
                            <button @click="closeQrCode" class="close-btn">×</button>
                        </div>

                        <div class="qr-content">
                            <div class="qr-code-container">
                                <img :src="generateQrCodeUrl(contact)" :alt="`${contact.title} QR Code`"
                                    class="qr-code-image">
                            </div>

                            <div class="qr-instructions">
                                <p class="qr-title">📱 Bu QR kodu tarayarak iletişim bilgilerini telefonunuza
                                    ekleyebilirsiniz</p>
                                <div class="qr-details">
                                    <h4>QR kod içeriği:</h4>
                                    <ul>
                                        <li><strong>İsim:</strong> {{ contact.title }}</li>
                                        <li v-if="contactQrTitle"><strong>Şirket:</strong> {{ contactQrTitle }}</li>
                                        <li v-if="contact.emailAddress"><strong>E-posta:</strong> {{ contact.emailAddress }}
                                        </li>
                                        <li v-if="contact.phoneNumber"><strong>Telefon:</strong> {{ contact.phoneNumber }}
                                        </li>
                                        <li v-if="contact.address"><strong>Adres:</strong> {{ contact.address }}</li>
                                        <li v-if="contact.website"><strong>Website:</strong> {{ contact.website }}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Contact Button (Outside the grid) -->
        <div v-if="showAddButton" class="add-contact-container">
            <button @click="startAddContact" class="add-contact-btn">
                {{ addButtonText || "İletişim Bilgisi Ekle" }}
            </button>
        </div>

        <!-- Add Contact Modal -->
            <teleport to="body">
            <transition appear type="animation" enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster">
                <ac-medium-modal v-if="isAddingContact || editingContact !== null" width="50vw" height="80vh"
                    @modal-closed="isAddingContact = false; editingContact = null; modalClosed()">
                    <contact-modal 
                        :is-visible="isAddingContact || editingContact !== null" 
                        :is-editing="editingContact !== null"
                        :existing-contact="editingContact !== null ? contacts[editingContact] : undefined"
                        @closed="isAddingContact = false; editingContact = null; modalClosed()"
                        @contact-saved="editingContact !== null ? handleContactUpdated($event, editingContact) : handleNewContactSaved($event)" />
                </ac-medium-modal>
            </transition>
        </teleport>
    </div>
</template>

<style lang="scss" scoped>
@import "../../styles/core/variables.scss";

.contact-displayer-wrapper {
    width: 100%;
}

.contact-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    align-items: stretch;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

/* Responsive grid behavior */
@media screen and (max-width: 1200px) {
    .contact-grid {
        grid-template-columns: repeat(3, 1fr) !important;
    }
}

@media screen and (max-width: 900px) {
    .contact-grid {
        grid-template-columns: repeat(2, 1fr) !important;
    }
}

@media screen and (max-width: 600px) {
    .contact-grid {
        grid-template-columns: 1fr !important;
    }
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 3rem;
    opacity: 0.7;
    grid-column: 1 / -1;
}

.empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
}

.empty-text {
    font-size: 1.6rem;
    color: $accent-gray;
    font-style: italic;
}

/* Contact Cards */
.contact-item {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%; /* Ensure item takes full grid cell width */
    min-width: 0; /* Allow flex item to shrink if needed */
}

.contact-card {
    background-color: $color-box-background;
    border: 1px solid $accent-gray-subtle;
    border-radius: 1rem;
    padding: 2rem;
    box-shadow: $main-box-shadow;
    transition: all 0.3s;
    display: flex;
    flex-direction: column;
    width: 100%; /* Take full width of grid cell */
    box-sizing: border-box; /* Include padding and border in width calculation */
}

.contact-card:hover {
    box-shadow: $hover-box-shadow;
}

/* Contact Header */
.contact-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid $accent-gray-subtle;
}

.contact-title {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex: 1;
    min-width: 0; 
}

.contact-icon {
    font-size: 2rem;
}

.contact-title h4 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: $text-color;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
    max-width: 100%; /* Ensure title doesn't exceed container */
}

/* Action Buttons */
.contact-actions {
    display: flex;
    gap: 0.8rem;
    flex-shrink: 0; 
}

.action-btn {
    background: none;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 1.4rem;
}

/* Contact Details */
.contact-details {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    flex: 1;
}

.contact-field {
    display: flex;
    align-items: flex-start;
    gap: 1.2rem;
    font-size: 1.4rem;
}

.field-icon-img {
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
}

.field-value {
    color: $text-color;
    word-wrap: break-word; /* Break long words if needed */
    overflow-wrap: break-word; /* Modern browsers */
    word-break: break-word; /* Additional support */
}

/* Links */
.email-link,
.phone-link,
.website-link {
    color: $accent-color;
    text-decoration: none;
    transition: all 0.3s;
}

.email-link:hover,
.phone-link:hover,
.website-link:hover {
    text-decoration: underline;
    color: $accent-color-dark;
}

/* No Contact Info */
.no-contact-info {
    padding: 1rem;
    text-align: center;
    color: $accent-gray;
    font-style: italic;
    background-color: $accent-gray-light;
    border-radius: 0.5rem;
}

/* QR Button Container */
.qr-button-container {
    padding-top: 1.5rem;
    display: flex;

    justify-content: center
}

.qr-save-btn {
    font-size: 1.4rem;
    padding: 0.8rem 1.6rem;
    transition: all 0.3s;
}

.qr-save-btn:hover {
    transform: translateY(-1px);
}

/* Add Button */
.add-contact-container {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 1rem;
}

.add-contact-btn {
    background-color: $accent-color;
    color: white;
    border: none;
    border-radius: 0.8rem;
    padding: 1rem 2rem;
    font-size: 1.4rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
}

.add-contact-btn:hover {
    background-color: $accent-color-dark;
    transform: translateY(-1px);
}

/* QR Modal Overlay */
.qr-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.qr-modal {
    background-color: $color-box-background;
    border-radius: 1rem;
    padding: 0;
    min-width: 50rem;
    max-width: 70rem;
    box-shadow: $main-box-shadow;
    overflow: hidden;
}

.qr-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2rem 3rem;
    border-bottom: 1px solid $accent-gray-subtle;
    background-color: $accent-gray-light;
}

.qr-header h3 {
    margin: 0;
    font-size: 2rem;
    color: $text-color;
}

.close-btn {
    background: none;
    border: none;
    font-size: 3rem;
    color: $accent-gray;
    cursor: pointer;
    transition: all 0.3s;
    padding: 0;
    line-height: 1;
}

.close-btn:hover {
    color: $text-color;
    transform: scale(1.1);
}

.qr-content {
    display: flex;
    gap: 3rem;
    padding: 3rem;
}

.qr-code-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
}

.qr-code-image {
    width: 30rem;
    height: 30rem;
    border: 1px solid $accent-gray-subtle;
    border-radius: 1rem;
    box-shadow: $main-box-shadow;
}

.qr-instructions {
    flex: 1;
}

.qr-title {
    font-size: 1.6rem;
    font-weight: 500;
    margin-bottom: 2rem;
    color: $text-color;
    text-align: center;
}

.qr-details h4 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: $text-color;
}

.qr-details ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.qr-details li {
    padding: 0.8rem 0;
    font-size: 1.4rem;
    border-bottom: 1px solid $accent-gray-light;
    color: $text-color;
}

.qr-details li:last-child {
    border-bottom: none;
}

.qr-details strong {
    color: $accent-color;
    min-width: 8rem;
    display: inline-block;
}
</style>
